/// index.js or MainApp.jsx

import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import store, { persistor } from './redux/store';

import { startTokenExpiryCheck } from './redux/actions';
import useLocalStorage from './functions/useLocalStorage';
import VersionChecker from './functions/VersionChecker';

import Homepage from './components/Homepage';
import User from './components/Users';
import PrivacyPolicy from './components/PrivacyPolicy';
import Support from './components/Support';
import ESGChecker from './components/ESGChecker.jsx';
import AIIdeasReview from './components/AIIdeasReview.jsx'; // DAS-1389
import Backtesting from './components/Backtesting.jsx';
import EngineeringAdventures from './components/EngineeringAdventures.jsx'; // DAS-1898
import PortfolioAnalysis from './components/PortfolioAnalysis.jsx'; // DAS-2096
import AlertDiscovery from './components/alert_discovery/AlertDiscovery.jsx'; // DAS-2367
import DriftWatch from './components/DriftWatch.jsx'; // DEV-only: agent instruction-drift demo


// Suppress ResizeObserver errors FIRST (before any other code)
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  // Patch the error handler at the earliest possible moment
  const originalError = console.error;
  console.error = function(...args) {
    const errorString = args.join(' ');
    if (errorString.includes('ResizeObserver loop')) {
      return; // Completely suppress ResizeObserver errors
    }
    originalError.apply(console, args);
  };

  // Also patch console.warn in case it shows there
  const originalWarn = console.warn;
  console.warn = function(...args) {
    const warnString = args.join(' ');
    if (warnString.includes('ResizeObserver loop')) {
      return;
    }
    originalWarn.apply(console, args);
  };

  // Suppress at the window level
  window.addEventListener('error', (e) => {
    if (e.message && e.message.includes('ResizeObserver loop')) {
      e.stopImmediatePropagation();
      e.preventDefault();
      return false;
    }
  }, true);

  // Patch React's error reporting (this is the key for the overlay)
  const throwError = (error) => {
    if (error && error.message && error.message.includes('ResizeObserver loop')) {
      return; // Don't throw ResizeObserver errors
    }
    throw error;
  };

  // Try to intercept the React DevTools overlay
  if (window.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
    const originalInject = window.__REACT_DEVTOOLS_GLOBAL_HOOK__.inject;
    window.__REACT_DEVTOOLS_GLOBAL_HOOK__.inject = function(...args) {
      console.log('React DevTools injecting...');
      return originalInject.apply(this, args);
    };
  }
}

if (process.env.NODE_ENV === 'development') {
  const whyDidYouRender = require('@welldone-software/why-did-you-render');
  whyDidYouRender(React);
}

const MainApp = () => {
  const dispatch = useDispatch();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showExpiredMessage, setShowExpiredMessage] = useState(false);
  const [userId, setUserId] = useState(null);
  const [token, setToken] = useLocalStorage('token', null);
  const tokenRef = useRef(localStorage.getItem('token'));

  useEffect(() => {
    const intervalId = startTokenExpiryCheck(dispatch, setShowExpiredMessage);
    return () => clearInterval(intervalId);
  }, [dispatch]);

  return (
    <React.StrictMode>
      {/* Only show version checker in production */}
      {process.env.NODE_ENV === 'production' && <VersionChecker />}
      <Router>
        <Routes>
          <Route path="/" element={
            <Homepage
              userId={userId}
              showLoginModal={showLoginModal}
              setShowLoginModal={setShowLoginModal}
              setToken={setToken}
              showExpiredMessage={showExpiredMessage}
            />
          } />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/support" element={<Support />} />
          <Route path="/esgcheck" element={<ESGChecker />} />
          <Route path="/ai-ideas" element={<AIIdeasReview />} /> {/* DAS-1389: Dev-only AI Ideas page */}
          <Route path="/backtesting" element={<Backtesting />} />
          <Route path="/engineering_adventures" element={<EngineeringAdventures />} /> {/* DAS-1898 */}
          <Route path="/portfolio_analysis" element={<PortfolioAnalysis />} /> {/* DAS-2096 */}
          <Route path="/alert_discovery" element={<AlertDiscovery />} /> {/* DAS-2367 */}
          <Route path="/drift-watch" element={<DriftWatch />} /> {/* DEV-only drift demo */}
        </Routes>
      </Router>
    </React.StrictMode>
  );
};

const rootElement = document.getElementById('root');

ReactDOM.createRoot(rootElement).render(
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <MainApp />
    </PersistGate>
  </Provider>
);


